import { createContext, useContext, type ReactNode } from 'react';

export const CANONICAL_TOUR_CONTRACT_ID = 'route42-core-v1' as const;
export const CANONICAL_TOUR_FIXTURE_URL = '/product-tour/route42-core-v1/fixture.json' as const;
export const CANONICAL_TOUR_ASSET_PREFIX = '/product-tour/route42-core-v1/evidence/' as const;

export type TourEvidenceStrength = 'STRONG' | 'MODERATE' | 'LIMITED';
export type TourEvidenceType = 'PDF' | 'CSV' | 'IMAGE';

export interface TourMachine {
  machine_id: string;
  machine_name: string;
  asset_id: string;
  machine_type: string;
  manufacturer: string;
  model: string;
  facility_name: string;
  location: string;
  production_area: string;
  status: string;
  description: string;
}

export interface TourClaim {
  claim_id: string;
  statement: string;
  evidence_strength: TourEvidenceStrength;
}

export interface TourEvidenceSource {
  document_id: string;
  evidence_type: TourEvidenceType;
  title: string;
  original_filename: string;
  mime_type: string;
  asset_path: string;
  sha256: string;
  uploaded_at: string;
  processing_status: string;
  role_in_story: string;
  preview: Record<string, string | number>;
  claims: TourClaim[];
}

export interface TourConversationMessage {
  message_id: string;
  sequence: number;
  role: 'USER' | 'ASSISTANT';
  created_at: string;
  text: string;
  evidence_claim_ids: string[];
}

export interface TourFinding {
  finding_id: string;
  title: string;
  statement: string;
  evidence_strength: TourEvidenceStrength;
  evidence_claim_ids: string[];
}

export interface TourHypothesis {
  hypothesis_id: string;
  title: string;
  status: 'PLAUSIBLE_UNCONFIRMED' | 'POSSIBLE_UNTESTED';
  evidence_strength: TourEvidenceStrength;
  supporting_evidence_claim_ids: string[];
  limitation: string;
}

export interface TourMemoryInsight {
  insight_id: string;
  title: string;
  statement: string;
  evidence_strength: TourEvidenceStrength;
  evidence_claim_ids: string[];
}

export interface TourMemoryRelationship {
  relationship_id: string;
  title: string;
  statement: string;
  evidence_strength: TourEvidenceStrength;
  evidence_claim_ids: string[];
}

export interface TourMemoryGap {
  gap_id: string;
  statement: string;
  why_it_matters: string;
}

export interface TourActivityItem {
  activity_id: string;
  occurred_at: string;
  category: 'DOCUMENTS' | 'CONVERSATIONS' | 'INVESTIGATIONS' | 'INTELLIGENCE';
  event_type: string;
  title: string;
  summary: string;
  resource_type: 'DOCUMENT' | 'CONVERSATION' | 'INVESTIGATION' | 'MACHINE_MEMORY';
  resource_id: string;
}

export interface TourCollaborativeParticipant {
  participant_id: string;
  name: string;
  role: string;
  actor_type: 'HUMAN' | 'AI';
}

export interface TourCollaborativeEvidence {
  evidence_id: string;
  label: string;
  kind: 'OBSERVATION' | 'REFERENCE' | 'HISTORY' | 'MEASUREMENT' | 'ACTION' | 'OUTCOME' | 'REVIEW' | 'CLOSURE';
  source_claim_ids: string[];
}

export interface TourCollaborativeMoment {
  moment_id: string;
  sequence: number;
  title: string;
  primary_participant_id: string;
  human_contribution: string;
  evidence: TourCollaborativeEvidence[];
  route42_contribution: string;
  understanding_before: string;
  understanding_after: string;
  next_action: string;
}

export interface TourCollaborativeInvestigationExample {
  example_id: string;
  title: 'Low discharge pressure on P-101';
  machine_id: string;
  teaching_goal: string;
  opening_assessment: string;
  participants: TourCollaborativeParticipant[];
  moments: TourCollaborativeMoment[];
  closing_assessment: string;
  narrative_rule: string;
}

export interface TourFixture {
  schema_version: '1.0';
  fixture_id: string;
  contract_id: typeof CANONICAL_TOUR_CONTRACT_ID;
  tour_id: 'route42-core';
  tour_version: 1;
  fixture_version: 1;
  status: 'CANONICAL';
  story: {
    title: string;
    machine_question: string;
    operator_goal: string;
    aha: string;
    fiction_notice: string;
  };
  determinism: {
    uses_real_customer_data: false;
    requires_live_ai: false;
    mutates_customer_resources: false;
    stable_resource_ids: true;
    fixed_timestamps: true;
    asset_integrity_sha256: true;
  };
  machine: TourMachine;
  evidence_sources: TourEvidenceSource[];
  conversation: {
    conversation_id: string;
    title: string;
    conversation_type: string;
    status: string;
    created_at: string;
    updated_at: string;
    guided_follow_up_options: string[];
    messages: TourConversationMessage[];
  };
  investigation: {
    investigation_id: string;
    title: string;
    status: string;
    source_conversation_id: string;
    created_at: string;
    updated_at: string;
    objective: string;
    current_assessment: string;
    pinned_document_ids: string[];
    findings: TourFinding[];
    hypotheses: TourHypothesis[];
    open_questions: string[];
  };
  collaborative_investigation_example: TourCollaborativeInvestigationExample;
  machine_memory: {
    machine_memory_id: string;
    memory_version: number;
    status: string;
    updated_at: string;
    summary: string;
    insights: TourMemoryInsight[];
    relationships: TourMemoryRelationship[];
    documentation_gaps: TourMemoryGap[];
    known_conflicts: unknown[];
  };
  activity: TourActivityItem[];
  ace_example: { title: string; prompt: string; answer: string };
}

function assertTourFixture(value: unknown): asserts value is TourFixture {
  if (!value || typeof value !== 'object') throw new Error('The Product Tour example could not be loaded.');
  const fixture = value as Partial<TourFixture>;
  if (
    fixture.contract_id !== CANONICAL_TOUR_CONTRACT_ID
    || fixture.tour_id !== 'route42-core'
    || fixture.tour_version !== 1
    || fixture.fixture_version !== 1
    || fixture.status !== 'CANONICAL'
  ) {
    throw new Error('The Product Tour example is not the canonical Route42 tour fixture.');
  }
  const d = fixture.determinism;
  if (!d || d.uses_real_customer_data !== false || d.requires_live_ai !== false || d.mutates_customer_resources !== false) {
    throw new Error('The Product Tour fixture does not satisfy Route42 isolation requirements.');
  }
  if (fixture.fixture_id !== 'route42-core-v1-canonical-fixture') {
    throw new Error('The Product Tour fixture identity is not canonical.');
  }
  assertTourId(fixture.machine?.machine_id, 'machine');

  const documentIds = new Set<string>();
  const claimIds = new Set<string>();
  for (const source of fixture.evidence_sources ?? []) {
    assertTourId(source.document_id, 'document');
    if (!isSafeTourAssetPath(source.asset_path)) throw new Error('The Product Tour fixture contains an unsafe evidence asset path.');
    documentIds.add(source.document_id);
    for (const claim of source.claims ?? []) {
      assertCanonicalToken(claim.claim_id, 'claim');
      claimIds.add(claim.claim_id);
    }
  }

  assertTourId(fixture.conversation?.conversation_id, 'conversation');
  for (const message of fixture.conversation?.messages ?? []) {
    assertTourId(message.message_id, 'conversation message');
    assertClaimReferences(message.evidence_claim_ids, claimIds);
  }

  assertTourId(fixture.investigation?.investigation_id, 'investigation');
  if (fixture.investigation?.source_conversation_id !== fixture.conversation?.conversation_id) {
    throw new Error('The Product Tour fixture contains an unsafe Investigation source reference.');
  }
  for (const documentId of fixture.investigation?.pinned_document_ids ?? []) {
    if (!documentIds.has(documentId)) throw new Error('The Product Tour fixture contains an unknown pinned evidence source.');
  }
  for (const finding of fixture.investigation?.findings ?? []) {
    assertTourId(finding.finding_id, 'finding');
    assertClaimReferences(finding.evidence_claim_ids, claimIds);
  }
  for (const hypothesis of fixture.investigation?.hypotheses ?? []) {
    assertTourId(hypothesis.hypothesis_id, 'hypothesis');
    assertClaimReferences(hypothesis.supporting_evidence_claim_ids, claimIds);
  }

  const collaborativeExample = fixture.collaborative_investigation_example;
  assertTourId(collaborativeExample?.example_id, 'collaborative Investigation example');
  if (collaborativeExample?.machine_id !== fixture.machine?.machine_id) {
    throw new Error('The Product Tour collaborative Investigation example references an unknown machine.');
  }
  if (collaborativeExample?.title !== 'Low discharge pressure on P-101') {
    throw new Error('The Product Tour collaborative Investigation example title is not canonical.');
  }

  const participantIds = new Set<string>();
  const humanParticipantIds = new Set<string>();
  for (const participant of collaborativeExample?.participants ?? []) {
    assertTourId(participant.participant_id, 'collaborative Investigation participant');
    if (participantIds.has(participant.participant_id)) {
      throw new Error('The Product Tour collaborative Investigation example contains duplicate participants.');
    }
    participantIds.add(participant.participant_id);
    if (participant.actor_type === 'HUMAN') humanParticipantIds.add(participant.participant_id);
  }
  if (participantIds.size !== 4 || humanParticipantIds.size !== 3) {
    throw new Error('The Product Tour collaborative Investigation example must contain three workers and Route42 AI.');
  }

  const moments = collaborativeExample?.moments ?? [];
  if (moments.length !== 7 || moments.some((moment, index) => moment.sequence !== index + 1)) {
    throw new Error('The Product Tour collaborative Investigation example must contain seven ordered moments.');
  }
  const momentIds = new Set<string>();
  const storyEvidenceIds = new Set<string>();
  for (const moment of moments) {
    assertTourId(moment.moment_id, 'collaborative Investigation moment');
    if (momentIds.has(moment.moment_id)) {
      throw new Error('The Product Tour collaborative Investigation example contains duplicate moments.');
    }
    momentIds.add(moment.moment_id);
    if (!humanParticipantIds.has(moment.primary_participant_id)) {
      throw new Error('Each Product Tour collaborative Investigation moment must be attributed to a known human participant.');
    }
    for (const evidence of moment.evidence ?? []) {
      assertTourId(evidence.evidence_id, 'collaborative Investigation evidence');
      if (storyEvidenceIds.has(evidence.evidence_id)) {
        throw new Error('The Product Tour collaborative Investigation example contains duplicate evidence identities.');
      }
      storyEvidenceIds.add(evidence.evidence_id);
      assertClaimReferences(evidence.source_claim_ids, claimIds);
    }
  }

  assertTourId(fixture.machine_memory?.machine_memory_id, 'Machine Memory');
  for (const insight of fixture.machine_memory?.insights ?? []) {
    assertTourId(insight.insight_id, 'Machine Memory insight');
    assertClaimReferences(insight.evidence_claim_ids, claimIds);
  }
  for (const relationship of fixture.machine_memory?.relationships ?? []) {
    assertTourId(relationship.relationship_id, 'Machine Memory relationship');
    assertClaimReferences(relationship.evidence_claim_ids, claimIds);
  }
  for (const gap of fixture.machine_memory?.documentation_gaps ?? []) assertTourId(gap.gap_id, 'Machine Memory gap');

  const knownResources = new Set([
    ...documentIds,
    fixture.conversation?.conversation_id,
    fixture.investigation?.investigation_id,
    fixture.machine_memory?.machine_memory_id,
  ].filter((id): id is string => Boolean(id)));
  for (const activity of fixture.activity ?? []) {
    assertTourId(activity.activity_id, 'activity');
    if (!knownResources.has(activity.resource_id)) throw new Error('The Product Tour fixture contains an unknown Activity resource reference.');
  }
}

function assertTourId(value: string | undefined, kind: string): asserts value is string {
  if (!value || !value.startsWith('tour_') || value.includes('#') || /[\r\n\0]/.test(value)) {
    throw new Error(`The Product Tour fixture contains an unsafe ${kind} identity.`);
  }
}

function assertCanonicalToken(value: string | undefined, kind: string): asserts value is string {
  if (!value || !/^[a-z0-9][a-z0-9-]{0,79}$/.test(value)) {
    throw new Error(`The Product Tour fixture contains an unsafe ${kind} identifier.`);
  }
}

function assertClaimReferences(values: string[] | undefined, knownClaimIds: Set<string>): void {
  for (const claimId of values ?? []) {
    if (!knownClaimIds.has(claimId)) throw new Error('The Product Tour fixture contains an unknown evidence claim reference.');
  }
}

export function isSafeTourAssetPath(path: string): boolean {
  return path.startsWith(CANONICAL_TOUR_ASSET_PREFIX)
    && !path.includes('..')
    && !path.includes('\\')
    && !path.includes('?')
    && !path.includes('#')
    && !path.startsWith('//');
}

export async function loadCanonicalTourFixture(fetcher: typeof fetch = fetch): Promise<TourFixture> {
  const response = await fetcher(CANONICAL_TOUR_FIXTURE_URL, {
    method: 'GET',
    credentials: 'same-origin',
    // The route42-core-v1 fixture can gain compatible teaching content while
    // the tour contract remains v1. Never let a browser-cached older fixture
    // run against a newer frontend validator.
    cache: 'no-store',
  });
  if (!response.ok) throw new Error('The Product Tour example could not be loaded.');
  const value: unknown = await response.json();
  assertTourFixture(value);
  return value;
}

const TourDataContext = createContext<TourFixture | null>(null);

export function TourDataBoundary({ fixture, children }: { fixture: TourFixture; children: ReactNode }) {
  return <TourDataContext.Provider value={fixture}>{children}</TourDataContext.Provider>;
}

export function useTourData(): TourFixture {
  const value = useContext(TourDataContext);
  if (!value) throw new Error('useTourData must be used inside the isolated Product Tour boundary.');
  return value;
}
